/**
 * The signed-in customer, as React sees them.
 *
 * What this context holds: a profile and a status. What it deliberately does NOT
 * hold: the access token. That stays in the module variable inside
 * lib/api/client.ts, where exactly one file can reach it — putting it in a
 * context value would spray it through the React tree and into every devtools
 * snapshot for no benefit, since no component needs to read it.
 *
 * Context and hook live in this .ts file (no components) so Fast Refresh keeps
 * working — the same rule the announcer follows.
 */
import { createContext, useContext } from 'react';

import type { CustomerProfile } from '@/lib/api/types';

export type SessionStatus =
  /** Bootstrapping: we are asking the refresh cookie whether it is anybody. */
  | 'loading'
  /** No session. A perfectly normal state — uploading does not require one. */
  | 'anonymous'
  | 'authenticated';

export interface SessionApi {
  status: SessionStatus;
  user: CustomerProfile | null;
  /** Called by the OTP form once verify succeeds. */
  signIn: (user: CustomerProfile, expiresInSeconds: number) => void;
  signOut: () => Promise<void>;
}

const noop: SessionApi = {
  status: 'anonymous',
  user: null,
  signIn: () => undefined,
  signOut: () => Promise.resolve(),
};

export const SessionContext = createContext<SessionApi>(noop);

export function useSession(): SessionApi {
  return useContext(SessionContext);
}
